import React, { useMemo } from 'react';
import {
  Box,
  Inscription,
  PlanStatuses,
  Preloader,
} from '@common-fe/common-fe';

import { EmployerStatus } from '@/modules/employer/employer.constants';

import { useEmployerTerminationModalStore } from '../store';

import { useEmployerTerminationPlanList } from './hooks/useEmployerTerminationPlanList';
import { useGetEmployerTerminationDataQuery } from './queries/useGetEmployerTerminationData.query';
import { EmployerTerminationDateForm } from './EmployerTerminationDateForm';
import { EmployerTerminationHsaTransferForm } from './EmployerTerminationHsaTransferForm';
import { EmployerTerminationInfoBanner } from './EmployerTerminationInfoBanner';
import { EmployerTerminationPlanList } from './EmployerTerminationPlanList';
import { EmployerTerminationPlansCoverageDatesList } from './EmployerTerminationPlansCoverageDatesList';

const PLAN_STATUSES = [
  PlanStatuses.ACTIVE,
  PlanStatuses.PENDING_TERMINATION,
];
      
const ViewEmployerTerminationModalForm: React.FC= () => {    
  const employerId = useEmployerTerminationModalStore((state) => state.employerId);
  const employerName = useEmployerTerminationModalStore((state) => state.employerName);
  const terminationDate = useEmployerTerminationModalStore((state) => state.terminationDate);
  const terminationStatus = useEmployerTerminationModalStore((state) => state.terminationStatus);

  const isPendingTermination = useMemo(() => {
    return terminationStatus
    && (terminationStatus === EmployerStatus.PendingTermination);
  }, [terminationStatus]);

  const currentPlanStatuses = useMemo(() => {
    if (terminationStatus && terminationStatus === EmployerStatus.TerminationInProgress) {
      return [...PLAN_STATUSES, PlanStatuses.TERMINATION_IN_PROGRESS];
    }
    if (terminationStatus && terminationStatus === EmployerStatus.Terminated) {
      return [...PLAN_STATUSES, PlanStatuses.TERMINATED, PlanStatuses.TERMINATION_IN_PROGRESS];
    }
    return PLAN_STATUSES;
  }, [terminationStatus]);

  const { terminationData, isLoading: terminationDataLoading } = useGetEmployerTerminationDataQuery(employerId || '');
    
  const { 
    isLoading: planListLoading,
    data,
    coverageData,
    isHsaAccountExist,
    hsaStartDate,
    hsaTransferOfAssets,
  } = useEmployerTerminationPlanList({
    orgId: employerId,
    terminationDate: terminationData?.terminationDate,
    viewMode: true,
    statuses: currentPlanStatuses,
    isTerminatedOrg: terminationStatus === EmployerStatus.TerminationInProgress || terminationStatus === EmployerStatus.Terminated,
  });
      
  return (
    <>
      <Box margin={{ bottom: 'spacing24' }} align="center">
        {isPendingTermination ? (
          <>
            <Box margin={{ vertical: 'spacing12' }}>
              <Inscription size="24px" weight={700} lineHeight="32px" textAlign="center">
                {employerName || 'Organization'} has planned termination on {terminationDate || ''}
              </Inscription>
            </Box>
            <Inscription textAlign="center" size="14px">
              Please note that you can edit or cancel termination configurations until {terminationDate || ''}.
            </Inscription>
          </>
        ) : (
          <Inscription size="24px" weight={700} lineHeight="32px" textAlign="center">
            Organization has terminated
          </Inscription>
        )}
      </Box>
    
      <Box data-testid="ViewEmployerTerminationModalForm_container">
        <Box direction="column">
          <Box
            round="container1Round"
            pad="spacing24"
            background="module"
            gap='spacing24'
          >
            <EmployerTerminationDateForm
              terminationDate={terminationData?.terminationDate}
              minDate={hsaStartDate}
              viewMode
            />
            {planListLoading || terminationDataLoading ? (
              <Preloader />
            ) : (
              <Box gap="spacing24">
                <EmployerTerminationPlanList
                  planList={data}
                />
                {data.length ? (
                  <Box gap="spacing24">
                    <EmployerTerminationPlansCoverageDatesList
                      planList={coverageData}
                    />
                  </Box>
                ) : null}
    
                {isHsaAccountExist ? (
                  <EmployerTerminationHsaTransferForm
                    defaultHsaTransferOfAssets={hsaTransferOfAssets}
                    viewMode
                  />
                ) : null}
              </Box>
            )}  
            <EmployerTerminationInfoBanner />
          </Box>
        </Box>
      </Box>
    </>
  );
};
      
export default ViewEmployerTerminationModalForm;
      